<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-ack-main">
    <div class="ack-options">
      <div class="ack-filters-table-controls">
        <div class="ack-filter-summary-container">
          <span>{{ ackFilterSummary }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="ackFilterIsOpen"
        >
          <div class="filter-content">
            <BoxContainer title="Ack. Letter Date" :isCheckBox="true" :isChecked.sync="filterByAckDate">
              <DateTimePicker
                placeholder="Start Ack. Letter Date"
                :value.sync="ackStartDate"
                :showTime="false"
                :isRequired="filterByAckDate"
                :errorMessage.sync="ackStartDateErr"
                :isDisabled="!filterByAckDate"
              />
              <DateTimePicker
                placeholder="End Ack. Letter Date"
                :value.sync="ackEndDate"
                :showTime="false"
                :isRequired="filterByAckDate"
                :errorMessage.sync="ackEndDateErr"
                :isDisabled="!filterByAckDate"
              />
            </BoxContainer>

            <BoxContainer title="Ack. Letter No." :isCheckBox="true" :isChecked.sync="filterByAckNo">
              <InputQuery
                placeholder="Start Ack. Letter No."
                :items="ackNoList"
                itemKey="Ack. Letter No"
                :showDesc="false"
                :selectedObject.sync="selectedStartAckNo"
                :search.sync="startAckNoSearch"
                :isRequired="filterByAckNo"
                :errorMessage.sync="startAckNoErr"
                :isDisabled="!filterByAckNo"
                :allowFreeText="true"
                validator="text,25"
              />
              <InputQuery
                placeholder="End Ack. Letter No."
                :items="ackNoList"
                itemKey="Ack. Letter No"
                :showDesc="false"
                :selectedObject.sync="selectedEndAckNo"
                :search.sync="endAckNoSearch"
                :isRequired="filterByAckNo"
                :errorMessage.sync="endAckNoErr"
                :isDisabled="!filterByAckNo"
                :allowFreeText="true"
                validator="text,25"
              />
            </BoxContainer>
            <BoxContainer title="Customer" :isCheckBox="true" :isChecked.sync="filterByCustomer">
              <InputQuery
                placeholder="Start Customer"
                :items="customerList"
                itemKey="Code - Customer"
                :showDesc="false"
                :selectedObject.sync="selectedStartCustomer"
                :isRequired="filterByCustomer"
                :errorMessage.sync="startCustomerErr"
                :isDisabled="!filterByCustomer"
                :hideColumns="['Code - Customer']"
                validator="text,105"
              />
              <InputQuery
                placeholder="End Customer"
                :items="customerList"
                itemKey="Code - Customer"
                :showDesc="false"
                :selectedObject.sync="selectedEndCustomer"
                :isRequired="filterByCustomer"
                :errorMessage.sync="endCustomerErr"
                :isDisabled="!filterByCustomer"
                :hideColumns="['Code - Customer']"
                validator="text,105"
              />
            </BoxContainer>
            <div class="refresh-button">
              <button @click="refreshClicked">Refresh</button>
            </div>
          </div>
        </ButtonFilter>
        <button
          class="select-deselect-all-button"
          :disabled="selectDeselectAllDisabled"
          @click="selectDeselectAllClicked"
        >
          {{ selectDeselectAllButtonText }}
        </button>
      </div>
      <div class="ack-tablebox">
        <TableBox
          title="Acknowledgement Letter"
          :tableData.sync="ackTableData"
          :selectedRow.sync="ackSelectedRow"
          :maxSelection="-1"
          :loading="ackTableLoading"
          :hideColumns="ackTableHiddenColumns"
        />
      </div>
      <div class="header-formatting-controls">
        <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showLetterHeadChecked" />
        <Dropdown
          style="width: 50%; padding-top: 5px"
          placeholder="Currency Format"
          :items="currencyFormatItems"
          itemKey="name"
          :selectedObject.sync="selectedCurrencyFormat"
          :isRequired="true"
          :errorMessage.sync="currencyFormatErr"
        />
      </div>
      <div class="call-to-action-buttons">
        <button @click="previewAckClicked">Preview Acknowledgement Letter</button>
      </div>
    </div>

    <div class="ack-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        @shareClicked="designerV3ShareClicked"
        @afterPrint="designerV3AfterPrint"
        ref="designerV3"
      />
      <div v-else class="user-instructions">
        <span
          >Select some Acknowledgement Letters and click on <strong>Preview Acknowledgement Letter</strong> to get
          started</span
        >
      </div>
    </div>

    <PopupV2
      class="email-ack-popup"
      :show="showEmailAckPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailAckPopupButtons"
      title="Email Acknowledgement Letter"
      width="60%"
      @buttonClicked="emailAckPopupButtonClicked"
    >
      <div class="email-ack-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailAckPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailAckPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailAckPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailAckPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailAckPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    selectDeselectAllDisabled: function () {
      return this.ackTableData.length == 0;
    },

    selectDeselectAllButtonText: function () {
      if (this.ackTableData.length == 0) {
        return 'Select All';
      }
      return this.ackTableData.length != this.ackSelectedRow.length ? 'Select All' : 'Deselect All';
    },
  },
  data: function () {
    return {
      userDetails: null,

      ackFilterSummary: '',
      ackFilterIsOpen: false,

      filterByAckDate: false,
      ackStartDate: null,
      ackStartDateErr: '',
      ackEndDate: null,
      ackEndDateErr: '',

      ackNoList: [],
      filterByAckNo: false,
      selectedStartAckNo: null,
      startAckNoSearch: '',
      startAckNoErr: '',
      selectedEndAckNo: null,
      endAckNoSearch: '',
      endAckNoErr: '',

      customerList: [],
      filterByCustomer: false,
      selectedStartCustomer: null,
      startCustomerErr: '',
      selectedEndCustomer: null,
      endCustomerErr: '',

      ackTableData: [],
      ackSelectedRow: [],
      ackTableLoading: false,
      ackTableHiddenColumns: ['Date Updated', 'Updated By', 'Date Created', 'Indicator'],

      showLetterHeadChecked: true,
      currencyFormatItems: [
        { name: 'Original Currency', APIValue: 'original' },
        { name: 'Local Currency', APIValue: 'local' },
      ],
      selectedCurrencyFormat: null,
      currencyFormatErr: '',

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      showEmailAckPopup: false,
      emailAckPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailAckPopupToMails: [],
      emailAckPopupCCMails: [],
      emailAckPopupBCCMails: [],
      emailAckPopupSubjectInputValue: '',
      emailAckPopupMessageText: '',
    };
  },
  props: {},
  methods: {
    selectDeselectAllClicked: function (_pointerEvent) {
      if (this.ackTableData.length !== this.ackSelectedRow.length) {
        this.ackSelectedRow = [];
        this.$nextTick(() => {
          this.ackSelectedRow = [...this.ackTableData];
        });
      } else {
        this.ackSelectedRow = [];
      }
    },

    refreshClicked: async function () {
      this.ackStartDateErr = '';
      this.ackEndDateErr = '';
      this.startAckNoErr = '';
      this.endAckNoErr = '';
      this.startCustomerErr = '';
      this.endCustomerErr = '';

      let error = false;

      if (this.filterByAckDate) {
        if (!this.ackStartDate) {
          this.ackStartDateErr = 'Start Ack. Letter Date is required';
          error = true;
        }

        if (!this.ackEndDate) {
          this.ackEndDateErr = 'End Ack. Letter Date is required';
          error = true;
        } else if (this.ackStartDate > this.ackEndDate) {
          this.ackEndDateErr = 'End Ack. Letter Date cannot be greater than Start Ack. Letter Date';
          error = true;
        }
      }

      if (this.filterByAckNo) {
        if (!this.selectedStartAckNo && !this.startAckNoSearch.trim()) {
          this.startAckNoErr = 'Start Ack. Letter No. is required';
          error = true;
        }

        if (!this.selectedEndAckNo && !this.endAckNoSearch.trim()) {
          this.endAckNoErr = 'End Ack. Letter No. is required';
          error = true;
        }
      }

      if (this.filterByCustomer) {
        if (!this.selectedStartCustomer) {
          this.startCustomerErr = 'Start Customer is required';
          error = true;
        }

        if (!this.selectedEndCustomer) {
          this.endCustomerErr = 'End Customer is required';
          error = true;
        }
      }

      if (error) return;

      this.ackFilterIsOpen = false;
      this.updateAckFilterSummary();
      await this.getFilteredAckLetterList();
    },

    updateAckFilterSummary: function () {
      const filterList = [];

      if (this.filterByAckDate) {
        filterList.push(
          `date from ${this.ackStartDate.formatString('yyyy-MM-dd')} to ${this.ackEndDate.formatString('yyyy-MM-dd')}`,
        );
      }

      if (this.filterByAckNo) {
        filterList.push(
          `Ack. Letter No. from ${this.selectedStartAckNo ? this.selectedStartAckNo['Ack. Letter No'] : this.startAckNoSearch} to ${this.selectedEndAckNo ? this.selectedEndAckNo['Ack. Letter No'] : this.endAckNoSearch}`,
        );
      }

      if (this.filterByCustomer) {
        filterList.push(
          `Customer No. from ${this.selectedStartCustomer['Customer Code'] ?? ''} to ${this.selectedEndCustomer['Customer Code'] ?? ''}`,
        );
      }

      this.ackFilterSummary = `Filter by ${filterList.join(', ')}`;
    },

    getFilteredAckLetterList: async function () {
      const requestBody = {
        startdate: this.filterByAckDate ? this.ackStartDate.formatString('yyyy-MM-dd') : '',
        enddate: this.filterByAckDate ? this.ackEndDate.formatString('yyyy-MM-dd') : '',
        startackno:
          this.filterByAckNo ?
            this.selectedStartAckNo ?
              this.selectedStartAckNo['Ack. Letter No']
            : this.startAckNoSearch
          : '',
        endackno:
          this.filterByAckNo ?
            this.selectedEndAckNo ?
              this.selectedEndAckNo['Ack. Letter No']
            : this.endAckNoSearch
          : '',
        startcustomer: this.filterByCustomer ? this.selectedStartCustomer['Customer Code'] : '',
        endcustomer: this.filterByCustomer ? this.selectedEndCustomer['Customer Code'] : '',
      };

      this.ackTableData = [];
      this.ackSelectedRow = [];
      this.ackTableLoading = true;

      const ackListing = await this.$apiCallV2('/AcknowledgementLetter/GetAckLetterListing', requestBody);
      this.ackTableData = ackListing
        .filter((ack) => ack['Indicator'] != 'C')
        .map((ack, i) => {
          return {
            ...ack,
            '#': i + 1,
          };
        });
      this.ackTableLoading = false;
    },

    previewAckClicked: async function () {
      if (this.ackSelectedRow.length == 0) {
        this.$showAlert(
          'error',
          'No Acknowledgement Letter Selected',
          'Please select at least one acknowledgement letter to proceed',
        );
        return;
      }

      if (!this.selectedCurrencyFormat) {
        this.currencyFormatErr = 'Currency Format is required!';
        return;
      }

      const requestBody = {
        acknoarray: this.ackSelectedRow.map((ack) => ack['Ack. Letter No']).join(','),
        coshortname: this.userDetails['CoRefname'],
        showletterhead: this.showLetterHeadChecked ? '1' : '0',
        currencyformat: this.selectedCurrencyFormat ? this.selectedCurrencyFormat['APIValue'] : 'ORIGINAL'
      };

      this.$fullscreenLoading('Getting Acknowledgement Letter');
      const results = await this.$apiCallV2('/AcknowledgementLetter/PrintAckLetter', requestBody);
      if (Array.isArray(results) && results[0].Results === '1') {
        this.$showAlert('error', 'Error Printing Acknowledgement Letter', results[0].ErrMsg, [{ text: 'OK' }]);
        this.$fullscreenLoading();
        return;
      }
      const ackDetails = results['Acknowledgement Letter'];

      ackDetails.General['Show Company Letter Head'] = ackDetails.General['Show Company Letter Head'] != '0';
      ackDetails.General['companyheader'] = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : ackDetails.General['companyheader'];
      // ackDetails.General['localcur'] =
      //   !ackDetails.General['Co Local Currency'].trim() || ackDetails.General['Co Local Currency'] == 'MYR' ?
      //     'RM'
      //   : ackDetails.General['Co Local Currency'];
      
      this.$fullscreenLoading('Preparing to Print');
      for (const ack of ackDetails.AckLetter) {
        ack['printDate'] = new Date().formatString('dd/MM/yyyy');
        ack['page'] = '{{jsreportPageNum}} of {{jsreportTotalPage}}';
        ack['billcu'] = `${ack['custNo']} ${ack['custName']}`;

        const addr = ack['addr'].split('\n').filter((line) => line.trim() != '');
        if (ack['postcode'].trim() != '' || ack['city'].trim() != '') {
          addr.push(`${ack['postcode']} ${ack['city']}`);
        }
        if (ack['state'].trim() != '' || ack['country'].trim() != '') {
          addr.push(`${ack['state']} ${ack['country']}`);
        }

        for (const [i, val] of addr.entries()) {
          if (i > 5) {
            ack['addrLine6'] += `<br>${val}`;
            continue;
          }
          ack[`addrLine${i + 1}`] = val;
        }

        ack['monthYear'] = new Date(ack['ackDate']).formatString('MMMM yyyy').toUpperCase();
        ack['showInvDN'] = ack['InvoiceDN'] && ack['InvoiceDN'].length > 0;
        ack['showCN'] = ack['CreditNote'] && ack['CreditNote'].length > 0;

        ack['totalInvAmt'] = this.formatAmount(ack['totalInvDN'] || 0);
        ack['totalCNAmt'] = this.formatAmount(ack['totalCN'] || 0);

        if (ack['InvoiceDN'] && ack['InvoiceDN'].length > 0) {
          ack['_invoiceDN'] = ack['InvoiceDN'].map((inv, i) => {
            return {
              no: i + 1,
              docDate: new Date(inv['docDate']).formatString(this.$store.state['preferredDateFormat']) ?? '',
              docNo: inv['docNo'] || '',
              jobNo: inv['jobNo'] || '',
              blBkg: inv['blBkg'] || '',
              custRef: inv['custRef'] || '',
              docAmt: this.formatAmount(inv['docAmt'] || 0),
              oriAmt: this.formatAmount(inv['oriAmt'] || 0),
              rebate: this.formatAmount(inv['rebate'] || 0),
              adjustment: this.formatAmount(inv['adjustment'] || 0),
            };
          });
        }

        if (ack['CreditNote'] && ack['CreditNote'].length > 0) {
          ack['_creditNote'] = ack['CreditNote'].map((cn, i) => {
            return {
              no: i + 1,
              docDate: new Date(cn['docDate']).formatString(this.$store.state['preferredDateFormat']) ?? '',
              docNo: cn['docNo'] || '',
              jobNo: cn['jobNo'] || '',
              docAmt: this.formatAmount(cn['docAmt'] || 0),
            };
          });
        }

        ack['acknowledge'] = 'Please acknowledge receipt of the above documents and return the duplicate copy to us. ';
        if (ackDetails.General['Co Short Name'] == 'NHD') {
          ack['acknowledge'] =
            'Please acknowledge receipt of the above documents and return the duplicate copy via email to perwakilannakhoda@gmail.com. ';
        }
        ack['preparedBy'] =
          ackDetails.General['Co Short Name'] == 'GFLS' ? ack['ackUserId'] : ackDetails.General['preparedby'];

        const qrText = `{"ARACKNO": "${ack['ackNo']}"}`;
        ack['QRImage'] = await this.$getQRURL(qrText, { size: 150, margin: 10 });
      }
      this.$fullscreenLoading();

      this.designerV3ReportFiles = Array.from({ length: ackDetails.AckLetter.length }, () => 'AckLetter.json');
      this.designerV3ReportData = ackDetails.AckLetter;
      this.designerV3SharedData = ackDetails.General;
      this.showDesignerV3 = true;
    },

    formatAmount: function (amount) {
      const stringAmt = amount.toString();
      let formattedAmt = '';
      if (Number(amount) < 0) {
        formattedAmt = `(${this.$currencyFormat(stringAmt.replaceAll(',', '')).replace('-', '')})`;
      } else {
        formattedAmt = this.$currencyFormat(stringAmt.replaceAll(',', ''));
      }
      return formattedAmt;
    },

    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailAckPopupToMails = this.designerV3ReportData[0].email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailAckPopupToMails = [];
      }
      this.emailAckPopupCCMails = [];
      this.emailAckPopupBCCMails = [];

      this.emailAckPopupSubjectInputValue = 'Acknowledgement Letter';
      this.emailAckPopupMessageText = `Acknowledgement Letter: ${shareLink}\n\n`;

      this.showEmailAckPopup = true;
    },

    designerV3AfterPrint: async function () {
      const requestBody = {
        report: 'ACK',
        ackarray: this.designerV3ReportData.map((ack) => ack['ackNo']).join(','),
      };
      const response = await this.$apiCallV2('/AcknowledgementLetter/UpdatePrintCount', requestBody);
      if (response !== '') {
        this.$showAlert(
          'error',
          'Fail to update print count',
          response?.ErrMsg.toString() ?? 'An error occured when trying to update print count',
          [{ text: 'OK' }],
        );
      }
    },

    emailAckPopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailAckPopup = false;
        return;
      }

      if (this.emailAckPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Email', "Please enter at least one valid 'To' email address", [
          { text: 'OK' },
        ]);
        return;
      }

      if (!this.emailAckPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedWithoutSubject === 'Cancel') return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'ACKLETTER',
            docno: this.designerV3ReportData[0]['ackNo'],
            docnofieldname: '',
            recipient: this.emailAckPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailAckPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailAckPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailAckPopupSubjectInputValue.trim(),
            message: this.emailAckPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/AcknowledgementLetter/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailSOAPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
  },

  async mounted() {
    const currentDate = new Date();
    let startDate = new Date(currentDate);
    startDate.setDate(-180);
    startDate = startDate.formatString('yyyy-MM-dd');
    const endDate = currentDate.formatString('yyyy-MM-dd');
    const dataToPost = {
      startdate: startDate,
      enddate: endDate,
    };

    this.selectedCurrencyFormat = this.currencyFormatItems.find((f) => f['APIValue'] == 'original');

    const mountedPromises = [];
    let mountedPromisesFulFilledCount = 0;

    mountedPromises.push(
      this.$apiCallV2('/AcknowledgementLetter/GetAckLetterListing', dataToPost).then((result) => {
        this.ackNoList = result.filter((ack) => ack['Indicator'] != 'C');
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLCUST' }).then((result) => {
        this.customerList = result.map((c) => {
          return {
            ...c,
            'Code - Customer': `${c['Customer Code']} - ${c['Customer Name']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    const queryParamDocumentNo = this.$route.query.ackno?.trim?.();
    if (queryParamDocumentNo) {
      this.filterByAckNo = true;
      this.startAckNoSearch = queryParamDocumentNo;
      this.endAckNoSearch = queryParamDocumentNo;
    } else {
      this.filterByAckDate = true;
      this.ackStartDate = new Date();
      this.ackEndDate = new Date();
    }

    this.updateAckFilterSummary();
    const getFilteredAckListPromise = this.getFilteredAckLetterList();

    await Promise.allSettled([...mountedPromises, getFilteredAckListPromise]).then((_promiseOutcomes) => {
      if (queryParamDocumentNo) {
        const matchingAck = this.ackNoList.find((ack) => ack['Ack. Letter No'] == queryParamDocumentNo);
        if (matchingAck) {
          this.selectedStartAckNo = matchingAck;
          this.selectedEndAckNo = matchingAck;
        }
      }
    });

    if (this.ackTableData.length == 1) {
      this.ackSelectedRow = [...this.ackTableData];
    }
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-ack-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .ack-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .ack-filters-table-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .ack-filter-summary-container {
        margin-right: 5px;
        align-content: center;
        text-align: end;
      }

      .filter-content {
        text-align: left;
        cursor: auto;

        .refresh-button {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .ack-tablebox {
      height: calc(100% - 150px);
    }

    > .header-formatting-controls {
      margin-top: 5px;
      width: 100%;
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-top: 15px;
      padding-bottom: 5px;
    }
  }

  > .ack-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-ack-popup {
    .email-ack-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
